import {
  CheckCircle2,
  MessageCircle,
  ShoppingBag
} from "lucide-react";

import {
  useEffect
} from "react";

import {
  Link,
  useLocation
} from "react-router";

import type {
  CreatedOrder
} from "../services/orders";

interface StoredOrder {
  order: CreatedOrder;
  whatsappUrl: string;
}

function loadStoredOrder():
StoredOrder | null {
  try {
    const value =
      sessionStorage.getItem(
        "rini_last_order"
      );

    return value
      ? JSON.parse(value)
      : null;
  } catch {
    return null;
  }
}

export function OrderSuccessPage() {
  const location =
    useLocation();

  const routeState =
    location.state as
      | StoredOrder
      | null;

  const stored =
    routeState ??
    loadStoredOrder();

  const order =
    stored?.order;

  const whatsappUrl =
    stored?.whatsappUrl ??
    "";

  useEffect(() => {
    if (
      !order ||
      !whatsappUrl
    ) {
      return;
    }

    const storageKey =
      `rini_whatsapp_auto_opened_${order.orderNumber}`;

    const alreadyOpened =
      sessionStorage.getItem(
        storageKey
      );

    if (alreadyOpened) {
      return;
    }

    /*
     * Mark it before redirecting so that when the customer
     * returns from WhatsApp, refreshes the success page,
     * or presses Back, WhatsApp will not keep reopening.
     */
    sessionStorage.setItem(
      storageKey,
      "1"
    );

    const timer =
      window.setTimeout(
        () => {
          window.location.assign(
            whatsappUrl
          );
        },
        1400
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    order,
    whatsappUrl
  ]);

  return (
    <main className="rini-section bg-[#fffdf8]">
      <div className="rini-container">
        <div className="mx-auto max-w-3xl text-center">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
            <CheckCircle2
              size={38}
            />
          </span>

          <p className="mt-7 text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            Order received
          </p>

          <h1 className="rini-heading mt-4 text-5xl font-semibold sm:text-6xl">
            Thank you for choosing RiNi
          </h1>

          {order ? (
            <>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-[#746d65]">
                Your order has been safely recorded.
                WhatsApp will open automatically with your order details.
                Simply press Send so the RiNi team can confirm payment and delivery.
              </p>

              <div className="mx-auto mt-9 max-w-lg rounded-[2rem] bg-[#efe3d2] p-7 text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#70845b]">
                  Order number
                </p>

                <p className="mt-2 font-serif text-2xl text-[#38251e]">
                  {order.orderNumber}
                </p>

                <div className="mt-6 border-t border-[#5e3b2c]/10 pt-5">
                  <div className="flex justify-between">
                    <span className="text-[#746d65]">
                      Order total
                    </span>

                    <span className="font-semibold text-[#5e3b2c]">
                      ₹
                      {Number(
                        order.totalAmount
                      ).toLocaleString(
                        "en-IN"
                      )}
                    </span>
                  </div>

                  <div className="mt-3 flex justify-between">
                    <span className="text-[#746d65]">
                      Payment
                    </span>

                    <span className="font-medium text-[#38251e]">
                      Manual confirmation
                    </span>
                  </div>
                </div>
              </div>

              {whatsappUrl ? (
                <a
                  href={
                    whatsappUrl
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="rini-button-primary mt-8"
                >
                  <MessageCircle
                    size={19}
                  />

                  Open WhatsApp again
                </a>
              ) : (
                <div className="mx-auto mt-8 max-w-xl rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">
                  WhatsApp ordering number is not configured yet.
                  Your order has still been saved successfully.
                </div>
              )}
            </>
          ) : (
            <p className="mx-auto mt-6 max-w-xl leading-7 text-[#746d65]">
              Your order has been submitted.
              RiNi will contact you shortly.
            </p>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              className="rini-button-secondary"
            >
              <ShoppingBag
                size={18}
              />

              Continue shopping
            </Link>

            <Link
              to="/"
              className="inline-flex items-center px-5 py-3 text-sm font-semibold text-[#5e3b2c]"
            >
              Return home
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
